import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DateUtils } from '@shared/date-utils';
import { PlanejamentoAulaService } from '../services/planejamento-aula.service';
import { PlanejamentoAula } from '../models/planejamento-aula.types';
import { getSituacaoLabel, getSituacaoBadgeClass } from '../utils/planejamento-aula.utils';

const CARREGANDO = 'Carregando...';

@Component({
  selector: 'app-planejamento-aula-list',
  templateUrl: './planejamento-aula-list.component.html',
  styleUrls: ['./planejamento-aula-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanejamentoAulaListComponent implements OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() registros: PlanejamentoAula[] = [];
  @Input() carregando = false;
  @Input() semCalendario = false;
  @Input() semPeriodoAvaliativo = false;

  @Output() visualizar = new EventEmitter<PlanejamentoAula>();

  get listaVazia(): boolean {
    return !this.semCalendario && !this.semPeriodoAvaliativo && !this.registros.length && !this.carregando;
  }

  constructor(
    private planejamentoService: PlanejamentoAulaService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['registros'] && this.registros?.length) {
      this.registros.forEach(registro => {
        if ((registro as any).disciplinasCalculadas === undefined) {
          this.carregarDisciplinas(registro);
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────

  formatarData(data: string): string {
    return DateUtils.formatarDataBrasileira(data);
  }

  /** Retorna a string de período formatada, incluindo data-fim quando diferente da data-início. */
  formatarPeriodo(registro: PlanejamentoAula): string {
    const inicio = this.formatarData(registro.data);
    if (!registro.dataFim || registro.dataFim === registro.data) { return inicio; }
    return `${inicio} — ${this.formatarData(registro.dataFim)}`;
  }

  carregarDisciplinas(registro: any): void {
    if (registro.disciplinasCalculadas !== undefined) { return; }

    registro.disciplinasCalculadas = CARREGANDO;

    this.planejamentoService.getItensConteudo(registro.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (itensConteudo: any[]) => {
        registro.disciplinasCalculadas = this.getDisciplinasCorretas({ itensConteudos: itensConteudo });
        this.cdr.markForCheck();
      },
      error: () => {
        registro.disciplinasCalculadas = '-';
        this.cdr.markForCheck();
      }
    });
  }

  isCarregandoDisciplinas(registro: any): boolean {
    return registro.disciplinasCalculadas === CARREGANDO;
  }

  getDisciplinasCorretas(registro: any): string {
    const setFinal    = new Set<string>();
    const setFallback = new Set<string>();

    for (const ic of (registro?.itensConteudos ?? [])) {
      for (const ie of (ic?.itensEducacionais ?? [])) {
        const nome = ie?.itemEducacional?.descricao || ie?.itemEducacional?.nome;
        if (nome) { setFinal.add(nome); }
      }

      const listaAulas: any[] = ic?.aulasPlanejadas?.length
        ? ic.aulasPlanejadas
        : (ic?.aulas ?? []);
      for (const aula of listaAulas) {
        const nome = aula?.itemEducacional?.descricao || aula?.itemEducacional?.nome;
        if (nome) { setFallback.add(nome); }
      }
    }

    const set = setFinal.size > 0 ? setFinal : setFallback;
    if (set.size === 0) { return '-'; }

    return Array.from(set)
      .sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }))
      .join(', ');
  }

  exibirSituacaoListagem(registro: PlanejamentoAula): boolean {
    const sit = registro?.situacaoAprovacaoRaw;
    if (sit === null || sit === undefined) { return false; }
    const valorStr = String(sit).trim().toLowerCase();
    return valorStr !== 'null' && valorStr !== '';
  }

  getSituacaoLabel   = getSituacaoLabel;
  getSituacaoBadgeClass = getSituacaoBadgeClass;

  trackByRegistro(_index: number, registro: PlanejamentoAula): number {
    return registro.id;
  }
}
